// The v2 widget registry — the canonical list of placeable widgets. Adding a
// widget = one entry here (the backend stores widgetKey opaquely, so no redeploy).
// Slice 1 ships ONE entry, Player Bio, as the canary that proves the whole
// drag/save/reload loop on live data.

import type { ComponentType, PointerEvent as ReactPointerEvent } from "react";
import type { PlayerRef, WidgetState } from "./types.js";
import BioWidget from "./widgets/BioWidget.js";
import MatchesWidget from "./widgets/MatchesWidget.js";
import { makeStatsWidget } from "./widgets/StatsWidget.js";

/** What every widget component receives. */
export interface WidgetProps {
  instanceId: string;
  players: PlayerRef[];
  state: WidgetState;
  /** Start dragging one of this widget's players as a token (drop it onto another
   *  widget to merge/replace, or onto empty canvas to spawn a Bio). Widgets that
   *  expose a player as a drag handle (Bio name, a stats column/legend) call this
   *  on pointerdown; the canvas shell owns the ghost + drop resolution. */
  onPlayerDragStart?: (e: ReactPointerEvent, ref: PlayerRef) => void;
}

export interface WidgetDef {
  key: string;
  title: string;
  /** Default placement size in grid cells. */
  defaultSize: { w: number; h: number };
  /** Shows a player search in its header + seeds from the sticky default player. */
  needsPlayer: boolean;
  /** Accepts a 2nd+ player into a comparison (stats cards, radar). */
  mergeable: boolean;
  /** Single-player, but a dropped player REPLACES the current one (Matches, maps). */
  replaceOnDrop: boolean;
  /** Max players the widget can hold (1 for single-player). */
  maxPlayers: number;
  component: ComponentType<WidgetProps>;
}

export const WIDGET_DEFS: WidgetDef[] = [
  {
    key: "player-bio",
    title: "Player Bio",
    defaultSize: { w: 3, h: 4 },
    needsPlayer: true,
    mergeable: false,
    replaceOnDrop: false,
    maxPlayers: 1,
    component: BioWidget,
  },
  {
    key: "matches",
    title: "Matches",
    defaultSize: { w: 5, h: 8 },
    needsPlayer: true,
    // Single-player Gantt; a dropped player REPLACES the one shown (Ben's rule).
    mergeable: false,
    replaceOnDrop: true,
    maxPlayers: 1,
    component: MatchesWidget,
  },
  // The five stat cards share ONE renderer (makeStatsWidget) — they differ only by
  // which metric category they pull. All mergeable (drag-to-combine, up to 5).
  // "Appearance" is a stat card too, but the palette groups it under Player.
  {
    key: "appearance",
    title: "Appearance",
    defaultSize: { w: 4, h: 7 },
    needsPlayer: true,
    mergeable: true,
    replaceOnDrop: false,
    maxPlayers: 5,
    component: makeStatsWidget("appearance"),
  },
  {
    key: "passing",
    title: "Passing",
    defaultSize: { w: 4, h: 7 },
    needsPlayer: true,
    mergeable: true,
    replaceOnDrop: false,
    maxPlayers: 5,
    component: makeStatsWidget("passing"),
  },
  {
    key: "attacking",
    title: "Attacking",
    defaultSize: { w: 4, h: 7 },
    needsPlayer: true,
    mergeable: true,
    replaceOnDrop: false,
    maxPlayers: 5,
    component: makeStatsWidget("attacking"),
  },
  {
    key: "defending",
    title: "Defending",
    defaultSize: { w: 4, h: 7 },
    needsPlayer: true,
    mergeable: true,
    replaceOnDrop: false,
    maxPlayers: 5,
    component: makeStatsWidget("defending"),
  },
  {
    key: "goalkeeping",
    title: "Goalkeeping",
    defaultSize: { w: 4, h: 7 },
    needsPlayer: true,
    mergeable: true,
    replaceOnDrop: false,
    maxPlayers: 5,
    // Outfielders have no keeper data → show a clear message / per-column note
    // rather than a table of zeros (see StatsWidget). The gate looks ONLY at
    // keeper-specific metrics — an outfielder has these at 0 but some team-context
    // metrics (e.g. opponent_np_shots) non-zero, which would otherwise read as "data".
    component: makeStatsWidget("goalkeeping", {
      emptyNoun: "goalkeeping",
      columnNote: "no keeper data",
      signalKeys: ["np_shots_faced", "goals_faced", "np_save_perc", "np_xg_faced", "obv_gk"],
    }),
  },
];

export function getWidgetDef(key: string): WidgetDef | undefined {
  return WIDGET_DEFS.find((d) => d.key === key);
}
